import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Minus,
  Plus,
  ShoppingCart,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Sparkles,
  MapPin,
  Lightbulb,
} from "lucide-react";
import { PaymentModal } from "@/components/PaymentModal";
import EmailHint from "@/components/EmailHint";
import { api, fileUrl, formatBRL, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGO_URL =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_c5288981-1cfe-4202-bbfa-1fa74e8ddd2d/artifacts/nf5at2sb_6eb7c94a-c17d-437b-a6c9-9665777e6cb6.png";

const lineKey = (item_id, size, variant) => `${item_id}|${size || ""}|${variant || ""}`;

export default function InscricaoPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({});
  const [cart, setCart] = useState([]);
  const [selectedSize, setSelectedSize] = useState({});
  const [selectedVariant, setSelectedVariant] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subscriber, setSubscriber] = useState({ name: "", email: "", phone: "", city: "" });
  const [paymentModal, setPaymentModal] = useState(null);
  const preAddedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const [itemsRes, settingsRes] = await Promise.all([
        api.get("/items"),
        api.get("/settings/public"),
      ]);
      setItems(itemsRes.data);
      setSettings(settingsRes.data || {});
      if (!settingsRes.data?.subscription_active) {
        toast.info("Inscrições estão fechadas no momento.");
        nav("/", { replace: true });
      }
    } catch {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-add 1× Entrada on first load — Entrada é o mínimo para se inscrever.
  useEffect(() => {
    if (items.length === 0 || preAddedRef.current) return;
    const entrada = items.find((i) => /^entrada$/i.test(i.name));
    if (entrada && cart.length === 0) {
      preAddedRef.current = true;
      setCart([{
        item_id: entrada.id,
        name: entrada.name,
        price: entrada.price,
        image_path: entrada.image_path,
        size: null,
        variant: null,
        quantity: 1,
      }]);
    } else {
      preAddedRef.current = true;
    }
  }, [items, cart.length]);

  // Suggest switching to Combo when Entrada + Camiseta + Almoço are all present manually.
  const suggestCombo = useMemo(() => {
    const has = (re) => cart.some((l) => re.test(l.name));
    const hasCombo = has(/combo/i);
    if (hasCombo) return false;
    return has(/^entrada$/i) && has(/camiseta/i) && has(/almo[cç]o/i);
  }, [cart]);

  const comboSavings = useMemo(() => {
    if (!suggestCombo) return 0;
    const combo = items.find((i) => /combo/i.test(i.name));
    if (!combo) return 0;
    const bundle = cart
      .filter((l) => /^entrada$/i.test(l.name) || /camiseta/i.test(l.name) || /almo[cç]o/i.test(l.name))
      .reduce((s, l) => s + l.price * l.quantity, 0);
    // savings = current bundle price - combo price (if a single combo replaces one of each)
    // We assume a 1-person combo replaces exactly one of each. Multi-quantity keeps the extras separate.
    return Math.max(0, bundle - combo.price);
  }, [suggestCombo, items, cart]);

  const switchToCombo = () => {
    const combo = items.find((i) => /combo/i.test(i.name));
    if (!combo) return;
    const camiseta = cart.find((l) => /camiseta/i.test(l.name));
    const almoco = cart.find((l) => /almo[cç]o/i.test(l.name));
    const size = camiseta?.size || (combo.sizes || [])[0] || null;
    const variant = almoco?.variant || (combo.variants || [])[0] || null;

    // Remove exactly one Entrada, one Camiseta, one Almoço from the cart.
    const removeOne = (predicate, list) => {
      const idx = list.findIndex(predicate);
      if (idx < 0) return list;
      const line = list[idx];
      if (line.quantity > 1) {
        const n = [...list];
        n[idx] = { ...line, quantity: line.quantity - 1 };
        return n;
      }
      return list.filter((_, i) => i !== idx);
    };
    let next = removeOne((l) => /^entrada$/i.test(l.name), cart);
    next = removeOne((l) => /camiseta/i.test(l.name), next);
    next = removeOne((l) => /almo[cç]o/i.test(l.name), next);

    // Add or increment Combo with the selected size/variant.
    const key = `${combo.id}|${size || ""}|${variant || ""}`;
    const cIdx = next.findIndex((l) => `${l.item_id}|${l.size || ""}|${l.variant || ""}` === key);
    if (cIdx >= 0) {
      const n = [...next];
      n[cIdx] = { ...n[cIdx], quantity: n[cIdx].quantity + 1 };
      next = n;
    } else {
      next = [...next, {
        item_id: combo.id,
        name: combo.name,
        price: combo.price,
        image_path: combo.image_path,
        size,
        variant,
        quantity: 1,
      }];
    }
    setCart(next);
    if (size) setSelectedSize((m) => ({ ...m, [combo.id]: size }));
    if (variant) setSelectedVariant((m) => ({ ...m, [combo.id]: variant }));
    toast.success("Trocado pelo Combo — economia aplicada!");
  };

  const addToCart = (item, size, variant) => {
    if ((item.sizes || []).length > 0 && !size) {
      toast.error("Selecione um tamanho antes de adicionar");
      return;
    }
    if ((item.variants || []).length > 0 && !variant) {
      toast.error(`Selecione ${item.variant_label || "uma opção"} antes de adicionar`);
      return;
    }
    setCart((c) => {
      const key = lineKey(item.id, size, variant);
      const idx = c.findIndex((l) => lineKey(l.item_id, l.size, l.variant) === key);
      if (idx >= 0) {
        const n = [...c];
        n[idx] = { ...n[idx], quantity: n[idx].quantity + 1 };
        return n;
      }
      return [...c, { item_id: item.id, name: item.name, price: item.price, image_path: item.image_path, size: size || null, variant: variant || null, quantity: 1 }];
    });
  };

  const decCart = (item_id, size, variant) =>
    setCart((c) =>
      c
        .map((l) =>
          lineKey(l.item_id, l.size, l.variant) === lineKey(item_id, size, variant)
            ? { ...l, quantity: l.quantity - 1 }
            : l
        )
        .filter((l) => l.quantity > 0)
    );

  const total = useMemo(() => cart.reduce((s, l) => s + l.price * l.quantity, 0), [cart]);
  const cartQty = (item) => {
    const hasSizes = (item.sizes || []).length > 0;
    const hasVariants = (item.variants || []).length > 0;
    const size = hasSizes ? selectedSize[item.id] : null;
    const variant = hasVariants ? selectedVariant[item.id] : null;
    if ((hasSizes && !size) || (hasVariants && !variant)) return 0;
    const line = cart.find(
      (l) => l.item_id === item.id && (l.size || null) === (size || null) && (l.variant || null) === (variant || null)
    );
    return line ? line.quantity : 0;
  };

  const validPhone = (v) => v.replace(/\D/g, "").length >= 10;
  const canSubmit =
    subscriber.name.trim().length >= 3 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(subscriber.email) &&
    validPhone(subscriber.phone) &&
    subscriber.city.trim().length >= 2;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Preencha nome, email, telefone e cidade");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/subscriptions", {
        name: subscriber.name,
        email: subscriber.email,
        phone: subscriber.phone,
        city: subscriber.city,
        items: cart.map((l) => ({
          item_id: l.item_id,
          name: l.name,
          price: l.price,
          quantity: l.quantity,
          image_path: l.image_path,
          size: l.size,
          variant: l.variant,
        })),
      });
      toast.success("Inscrição registrada!");
      setCart([]);
      setSelectedSize({});
      setSelectedVariant({});
      setSubscriber({ name: "", email: "", phone: "", city: "" });

      const links = data.payment_links || [];
      const pix = data.pix || null;
      const txid = data.txid || "";
      const trackUrl = data.track_url || (txid ? `/pedido/${txid}` : "");
      if (links.length === 1 && !pix) {
        window.open(links[0].url, "_blank", "noopener,noreferrer");
      } else if (links.length > 0 || pix) {
        setPaymentModal({ links, pix, txid, trackUrl });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend retorna 409 com { code: "already_subscribed", txid, track_url, customer_name }
      if (e.response?.status === 409 && detail?.code === "already_subscribed") {
        toast.error(
          `Este e-mail já possui uma inscrição${detail.customer_name ? ` — ${detail.customer_name}` : ""}. Redirecionando…`,
          { duration: 5000 }
        );
        if (detail.track_url) {
          setTimeout(() => { window.location.href = detail.track_url; }, 1500);
        } else if (detail.txid) {
          setTimeout(() => nav(`/pedido/${detail.txid}`), 1500);
        }
        return;
      }
      toast.error(formatApiErrorDetail(detail) || "Erro ao enviar inscrição");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-cream)" }}>
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-cream)" }} data-testid="inscricao-page">
      <nav className="sticky top-0 z-30 bg-[#fdfbf7]/90 backdrop-blur-xl border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Renascer" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display text-lg font-bold">Renascer</div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500">Inscrição presencial</div>
            </div>
          </Link>
          <Link to="/" className="text-xs sm:text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar
          </Link>
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200 text-xs uppercase tracking-widest">
          <Sparkles className="h-3 w-3" /> Inscrição presencial · 20 de setembro de 2026
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-4 text-stone-900" data-testid="inscricao-title">
          Confirme sua presença
        </h1>
        <p className="text-stone-600 mt-3 max-w-xl">
          Preencha seus dados e, se quiser, já garanta os itens da loja do evento.
        </p>

        <div className="mt-8 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 bg-white rounded-2xl border border-stone-200 card-warm-shadow p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold">Seus dados</div>
              <h2 className="font-display text-2xl font-semibold">Dados do participante</h2>
            </div>
            <div>
              <Label htmlFor="s-name">Nome completo</Label>
              <Input id="s-name" data-testid="sub-name-input" value={subscriber.name} onChange={(e) => setSubscriber({ ...subscriber, name: e.target.value })} placeholder="Ex: Maria da Silva" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="s-email">Email</Label>
                <Input id="s-email" type="email" data-testid="sub-email-input" value={subscriber.email} onChange={(e) => setSubscriber({ ...subscriber, email: e.target.value })} placeholder="maria@email.com" />
                <EmailHint
                  value={subscriber.email}
                  onAccept={(fixed) => setSubscriber({ ...subscriber, email: fixed })}
                  testid="sub-email-hint"
                />
              </div>
              <div>
                <Label htmlFor="s-phone">Telefone</Label>
                <Input id="s-phone" data-testid="sub-phone-input" value={subscriber.phone} onChange={(e) => setSubscriber({ ...subscriber, phone: e.target.value })} placeholder="(11) 99999-9999" />
              </div>
            </div>
            <div>
              <Label htmlFor="s-city" className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Cidade</Label>
              <Input id="s-city" data-testid="sub-city-input" value={subscriber.city} onChange={(e) => setSubscriber({ ...subscriber, city: e.target.value })} placeholder="Ex: Cidade Ocidental — GO" />
            </div>
          </div>

          <div className="lg:col-span-2 bg-gradient-to-br from-stone-900 to-stone-800 text-white rounded-2xl p-6 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-300/80">Resumo</div>
              <div className="mt-4 space-y-1 text-sm">
                {cart.length === 0 ? (
                  <div className="text-stone-400">Nenhum item selecionado ainda.</div>
                ) : (
                  cart.map((l) => (
                    <div key={lineKey(l.item_id, l.size, l.variant)} className="flex justify-between border-b border-stone-700 py-1">
                      <span>
                        {l.quantity}× {l.name}
                        {l.size && <span className="text-xs text-stone-400"> [{l.size}]</span>}
                        {l.variant && <span className="text-xs text-stone-400"> ({l.variant})</span>}
                      </span>
                      <span>{formatBRL(l.price * l.quantity)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="mt-6">
              <div className="flex justify-between text-sm text-stone-300">
                <span>Total</span>
                <span className="font-display text-3xl font-bold brand-text" data-testid="sub-total">{formatBRL(total)}</span>
              </div>
              <button
                onClick={submit}
                disabled={!canSubmit || submitting}
                data-testid="sub-submit-btn"
                className="btn-primary-flame w-full mt-4 py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {submitting ? "Enviando..." : "Confirmar inscrição"}
              </button>
              <p className="text-[11px] text-stone-400 text-center mt-2">
                Após confirmar, o(s) link(s) de pagamento serão exibidos e enviados por email.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <h2 className="font-display text-2xl font-bold text-stone-900">Itens do evento (opcional)</h2>
        <p className="text-stone-600 mt-1">
          A <strong>Entrada</strong> já foi adicionada automaticamente. Você pode remover ou incluir outros itens abaixo.
        </p>

        {suggestCombo && (
          <div
            data-testid="combo-suggestion"
            className="mt-6 rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <div className="h-11 w-11 rounded-full flame-gradient text-white flex items-center justify-center shrink-0">
              <Lightbulb className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-stone-900">Que tal trocar pelo Combo?</div>
              <div className="text-sm text-stone-700">
                Você tem <strong>Entrada + Camiseta + Almoço</strong> no carrinho. O <strong>Combo</strong> junta os três com{" "}
                {comboSavings > 0 ? (
                  <>desconto de <strong>{formatBRL(comboSavings)}</strong>.</>
                ) : (
                  <>preço promocional.</>
                )}
              </div>
            </div>
            <button
              onClick={switchToCombo}
              data-testid="switch-to-combo-btn"
              className="btn-primary-flame px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 shrink-0"
            >
              Alterar para Combo <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => {
            const sizes = it.sizes || [];
            const variants = it.variants || [];
            const variantLabel = it.variant_label || "Opção";
            const hasSizes = sizes.length > 0;
            const hasVariants = variants.length > 0;
            const size = hasSizes ? selectedSize[it.id] || null : null;
            const variant = hasVariants ? selectedVariant[it.id] || null : null;
            const qty = cartQty(it);
            return (
              <article key={it.id} data-testid={`sub-item-${it.id}`} className="bg-white rounded-2xl border border-stone-200 card-warm-shadow overflow-hidden flex flex-col">
                <div className="aspect-[4/3] bg-stone-100">
                  {it.image_path ? <img src={fileUrl(it.image_path)} alt={it.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-stone-300"><ShoppingCart className="h-10 w-10" /></div>}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-display text-lg font-semibold">{it.name}</h3>
                  {it.description && <p className="text-xs text-stone-600 mt-1">{it.description}</p>}
                  {hasSizes && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Tamanho</div>
                      <div className="flex flex-wrap gap-1">
                        {sizes.map((s) => (
                          <button key={s} type="button" onClick={() => setSelectedSize((m) => ({ ...m, [it.id]: s }))} className={`px-2.5 h-8 rounded-full text-xs font-semibold border transition-all ${size === s ? "flame-gradient text-white border-transparent" : "bg-white text-stone-700 border-stone-300"}`}>{s}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {hasVariants && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">{variantLabel}</div>
                      <div className="flex flex-wrap gap-1">
                        {variants.map((v) => (
                          <button key={v} type="button" onClick={() => setSelectedVariant((m) => ({ ...m, [it.id]: v }))} className={`px-2.5 h-8 rounded-full text-xs font-semibold border transition-all ${variant === v ? "flame-gradient text-white border-transparent" : "bg-white text-stone-700 border-stone-300"}`}>{v}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-auto pt-3 flex items-center justify-between">
                    <div className="brand-text font-display text-lg font-bold">{formatBRL(it.price)}</div>
                    {qty === 0 ? (
                      <button
                        onClick={() => addToCart(it, size, variant)}
                        disabled={(hasSizes && !size) || (hasVariants && !variant)}
                        className="btn-primary-flame px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" /> Adicionar
                      </button>
                    ) : (
                      <div className="inline-flex items-center gap-1 border border-stone-300 rounded-full p-0.5">
                        <button onClick={() => decCart(it.id, size, variant)} className="h-7 w-7 rounded-full hover:bg-stone-100 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                        <span className="min-w-[20px] text-center text-sm font-semibold">{qty}</span>
                        <button onClick={() => addToCart(it, size, variant)} className="h-7 w-7 rounded-full flame-gradient text-white flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <PaymentModal payment={paymentModal} onClose={() => setPaymentModal(null)} kind="subscription" />
    </div>
  );
}
